import * as React from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { Check, Mail } from "lucide-react";

import { useAuthStore } from "@/store/authStore";
import { AuthShell } from "@/components/auth/AuthShell";
import {
  AuthBanner,
  AuthField,
  AuthFoot,
  AuthHeading,
  AuthInput,
  AuthLink,
  AuthPrimaryButton,
  CodeInput,
  PasswordInput,
  PasswordRules,
  passwordMeetsPolicy,
} from "@/components/auth/AuthForm";
import { normalizeEmail } from "@/lib/email";

/**
 * /login — canvas 01b `auth/Main`, `LoginError`, `LoginMfa`,
 * `LoginNewPassword`, `LoginMobile` (MV2-907).
 *
 * One shell, one 384 px column, and a real step machine on the auth
 * store's `pendingMfaChallenge`:
 *
 *   password  →  totp (SOFTWARE_TOKEN_MFA)
 *             →  new_password (NEW_PASSWORD_REQUIRED)  →  totp | signed in
 *
 * `?next=` is honoured on every viewport (it used to be dropped on
 * mobile); with no `next` we land on `/`, where the hand-off picks the
 * persona's home (MV2-910). Every error is one sentence that says what
 * to do.
 */

/** `?next=` must be an in-app path — never an absolute URL we could be bounced to. */
function safeNext(raw: string | null): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
}

export function LoginPage(): React.ReactElement {
  const status = useAuthStore((s) => s.status);
  const signInWithCredentials = useAuthStore((s) => s.signInWithCredentials);
  const confirmMfaCode = useAuthStore((s) => s.confirmMfaCode);
  const completeNewPassword = useAuthStore((s) => s.completeNewPassword);
  const cancelMfaChallenge = useAuthStore((s) => s.cancelMfaChallenge);
  const pending = useAuthStore((s) => s.pendingMfaChallenge);
  const pendingEmail = useAuthStore((s) => s.email);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));

  // Query hints from /accept-invite and /forgot-password success redirects.
  const justAccepted = params.get("accepted") === "1";
  const justReset = params.get("reset") === "1";

  const [email, setEmail] = React.useState(params.get("email") ?? "");
  const [password, setPassword] = React.useState("");
  const [code, setCode] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  if (status === "authenticated") {
    return <Navigate to={next} replace />;
  }

  const step: "password" | "totp" | "new_password" =
    pending?.kind ?? "password";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // MV2-350: lowercase the email so Cognito's case-sensitive
      // username_attributes=["email"] lookup matches regardless of how
      // the user typed it. Backend mirrors this at /token + /challenge.
      await signInWithCredentials(normalizeEmail(email), password);
      // A challenge leaves `pendingMfaChallenge` set and the page pivots
      // on the next render — only navigate when we are actually in.
      if (useAuthStore.getState().status === "authenticated") {
        navigate(next, { replace: true });
      }
    } catch (err) {
      setError(credentialsSentence(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTotp(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await confirmMfaCode(code);
      navigate(next, { replace: true });
    } catch {
      // Cognito raises `CodeMismatchException` on a bad code; stay on the
      // step and say what to do.
      setError(
        "That code didn't match — check your authenticator app and try again.",
      );
      setCode("");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!passwordMeetsPolicy(newPassword) || newPassword !== confirm) return;
    setError(null);
    setSubmitting(true);
    try {
      const outcome = await completeNewPassword(newPassword);
      if (outcome === "signed_in") {
        navigate(next, { replace: true });
      }
      // "totp" — the store now holds the TOTP challenge and the page
      // pivots to the code step on this render.
    } catch (err) {
      setError(
        err instanceof Error && /InvalidPassword/i.test(err.name + err.message)
          ? "Cognito rejected that password — it must meet every rule below."
          : err instanceof Error && err.message
            ? err.message
            : "We couldn't set that password. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  function switchAccount() {
    cancelMfaChallenge();
    setCode("");
    setPassword("");
    setNewPassword("");
    setConfirm("");
    setError(null);
  }

  // "Passwords don't match yet." is live — the board draws it mid-typing.
  const confirmMismatch = confirm.length > 0 && confirm !== newPassword;

  if (step === "totp") {
    return (
      <AuthShell>
        <form onSubmit={handleTotp} noValidate>
          <AuthHeading
            title="Two-step code"
            sub="Enter the 6-digit code from your authenticator app."
          />
          {error && (
            <AuthBanner tone="bad" className="mb-[18px]">
              {error}
            </AuthBanner>
          )}
          <CodeInput
            value={code}
            onChange={setCode}
            size="lg"
            autoFocus
            aria-label="6-digit code"
            disabled={submitting}
          />
          <p className="mt-2 text-[11px] leading-[1.4] text-gray-500">
            Codes change every 30 seconds.
            {pendingEmail ? ` Signed in as ${pendingEmail}.` : ""}
          </p>
          <div className="mt-5">
            <AuthPrimaryButton
              type="submit"
              loading={submitting}
              loadingLabel="Verifying…"
              disabled={code.length !== 6}
            >
              Verify
            </AuthPrimaryButton>
          </div>
          <AuthFoot
            left={
              <AuthLink onClick={switchAccount}>
                Use a different account
              </AuthLink>
            }
            right="Lost your device? Contact your administrator"
          />
        </form>
      </AuthShell>
    );
  }

  if (step === "new_password") {
    return (
      <AuthShell>
        <form onSubmit={handleNewPassword} noValidate>
          <AuthHeading
            title="Choose a new password"
            sub="Your administrator set a temporary password. Pick your own to continue."
          />
          {error && (
            <AuthBanner tone="bad" className="mb-[18px]">
              {error}
            </AuthBanner>
          )}
          <div className="flex flex-col gap-3.5">
            <AuthField label="New password" htmlFor="login-new-password">
              <PasswordInput
                id="login-new-password"
                autoFocus
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={submitting}
              />
            </AuthField>
            <PasswordRules password={newPassword} />
            <AuthField
              label="Confirm"
              htmlFor="login-confirm-password"
              err={confirmMismatch ? "Passwords don't match yet." : undefined}
            >
              <PasswordInput
                id="login-confirm-password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                invalid={confirmMismatch}
                disabled={submitting}
              />
            </AuthField>
          </div>
          <div className="mt-5">
            <AuthPrimaryButton
              type="submit"
              loading={submitting}
              loadingLabel="Setting password…"
              disabled={
                !passwordMeetsPolicy(newPassword) || newPassword !== confirm
              }
            >
              Set password and sign in
            </AuthPrimaryButton>
          </div>
          <AuthFoot
            left={
              <AuthLink onClick={switchAccount}>
                Use a different account
              </AuthLink>
            }
          />
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form onSubmit={handleSubmit} noValidate>
        <AuthHeading
          title="Sign in"
          sub="Welcome back — enter your credentials to continue."
        />

        {(justAccepted || justReset) && !error && (
          <AuthBanner tone="info" className="mb-[18px]">
            <span className="inline-flex items-center gap-2">
              <Check
                size={13}
                aria-hidden
                className="text-[color:var(--success)]"
              />
              {justAccepted
                ? "Your password is set. Sign in below to continue."
                : "Password reset. Sign in with your new password."}
            </span>
          </AuthBanner>
        )}

        {error && (
          <AuthBanner tone="bad" className="mb-[18px]">
            {error === WRONG_CREDENTIALS ? (
              <>
                That email and password don&apos;t match. Check both and try
                again, or{" "}
                <AuthLink
                  to={`/forgot-password${email ? `?email=${encodeURIComponent(normalizeEmail(email))}` : ""}`}
                >
                  reset your password
                </AuthLink>
                .
              </>
            ) : (
              error
            )}
          </AuthBanner>
        )}

        <div className="flex flex-col gap-3.5">
          <AuthField label="Email" htmlFor="login-email">
            <AuthInput
              id="login-email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              icon={<Mail size={15} />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </AuthField>
          <AuthField label="Password" htmlFor="login-password">
            <PasswordInput
              id="login-password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              invalid={error === WRONG_CREDENTIALS}
              disabled={submitting}
            />
          </AuthField>
        </div>

        <div className="mt-5">
          <AuthPrimaryButton
            type="submit"
            loading={submitting}
            loadingLabel="Signing in…"
            disabled={!email.trim() || !password}
          >
            Sign in
          </AuthPrimaryButton>
        </div>

        <AuthFoot
          left={
            <AuthLink
              to={`/forgot-password${email ? `?email=${encodeURIComponent(normalizeEmail(email))}` : ""}`}
            >
              Forgot password?
            </AuthLink>
          }
        />
      </form>
    </AuthShell>
  );
}

/** Sentinel so the render can draw the board's wrong-credentials banner with its link. */
const WRONG_CREDENTIALS = "__wrong_credentials__";

/**
 * Cognito's `NotAuthorizedException` / `UserNotFoundException` both become
 * the one board sentence — never confirm which half was wrong. Anything else
 * (network, MFA setup) keeps its own message.
 */
function credentialsSentence(err: unknown): string {
  const name = err instanceof Error ? err.name : "";
  const message = err instanceof Error ? err.message : "";
  if (
    /NotAuthorized|UserNotFound|Incorrect username or password/i.test(
      `${name} ${message}`,
    )
  ) {
    return WRONG_CREDENTIALS;
  }
  if (/Network|Failed to fetch/i.test(message)) {
    return "We couldn't reach the sign-in service. Check your connection and try again.";
  }
  return (
    message || "Sign-in failed. Check your email and password and try again."
  );
}
